import React, { useState, useEffect } from 'react';
import './AuctionReports.css';
import ReportService, { AuctionReport, AuctionBidSummary } from '../../../services/reportService';
import { format } from 'date-fns';
import { useAuth } from '../../../contexts/AuthContext';
import { FileText, Trophy, Gavel, Users, TrendingDown, Info, Download } from 'lucide-react';
import { auctionTypeLabel, auctionModeLabel } from '../../../utils/auctionTypeLabels';
import { isForwardMode, rankLabel } from '../../../utils/auctionDirection';
import { generateAuctionReport } from '../Separate_MyCreated/AuctionReportGenerator';

interface BaseAuction {
  id: string;
  auctionNo: string;
  title: string;
  auctionDetails: string;
  auctionDate: string;
  auctionStartTime: string;
  auctionEndTime: string; // ADD THIS FIELD
  duration?: number;
  currency: string;
  decrementalValue?: string;
  auctionType?: string;
  auctionMode?: string;
}

interface AuctionParticipant {
  id: string;
  userId: string;
  companyName: string;
  preBidOffer?: number;
  finalBid?: number;
  rank?: string;
}

const AuctionReports: React.FC = () => {
  const { user } = useAuth();
  const [auctions, setAuctions] = useState<BaseAuction[]>([]);
  const [selectedAuction, setSelectedAuction] = useState<BaseAuction | null>(null);
  const [participants, setParticipants] = useState<AuctionParticipant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const safeDateParse = (dateString: string): string => {
    try {
      if (!dateString) return new Date().toISOString().split('T')[0];
      
      // If it's already in YYYY-MM-DD format, return as is
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateString;
      }
      
      let date: Date;
      if (dateString.includes('T')) {
        // Parse ISO string but extract just the date part to avoid timezone issues
        const isoDate = new Date(dateString);
        if (isNaN(isoDate.getTime())) return new Date().toISOString().split('T')[0];
        // Create a new date in local timezone to avoid offset issues
        const localDate = new Date(isoDate.getFullYear(), isoDate.getMonth(), isoDate.getDate());
        return localDate.toISOString().split('T')[0];
      } else {
        // For simple date strings, parse without adding time to avoid timezone shifts
        const parts = dateString.split('-');
        if (parts.length === 3) {
          const year = parseInt(parts[0]);
          const month = parseInt(parts[1]) - 1; // Month is 0-indexed
          const day = parseInt(parts[2]);
          date = new Date(year, month, day);
        } else {
          date = new Date(dateString + 'T00:00:00');
        }
      }
      
      if (isNaN(date.getTime())) return new Date().toISOString().split('T')[0];
      return date.toISOString().split('T')[0];
    } catch {
      return new Date().toISOString().split('T')[0];
    }
  };

  // Function to convert 12-hour time back to 24-hour for calculations
  const convert12to24 = (time12h: string): string => {
    if (!time12h) return '00:00';
    
    const [time, modifier] = time12h.split(' ');
    let [hours, minutes] = time.split(':');
    
    if (hours === '12') {
      hours = '00';
    }
    
    if (modifier === 'PM') {
      hours = (parseInt(hours, 10) + 12).toString();
    }
    
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
  };

  const transformAuctionData = (report: AuctionReport): BaseAuction => ({

    id: report.id.toString(),
    auctionNo: `AUC-${report.id.toString().padStart(3, '0')}`,
    title: report.title,
    auctionDetails: report.auction_details || report.description,
    auctionDate: safeDateParse(report.auctionDate || report.auction_date),
    // Keep the 12-hour format for display, but store original for calculations
    auctionStartTime: report.auctionStartTime || report.start_time || '10:00 AM',
 auctionEndTime: report.end_time || '11:00 AM', // ADD THIS - use end_time from API
    duration: 60,
    currency: report.currency || 'INR',
    decrementalValue: report.decremental_value,
    auctionType: report.auction_type,
    auctionMode: report.auction_mode
  });

  const transformParticipantsData = (bids: AuctionBidSummary[], mode?: string): AuctionParticipant[] =>
    bids.map((bid, index) => ({
      id: `participant-${index}`,
      userId: `user-${index}`,
      companyName: bid.company_name,
      preBidOffer: parseFloat(bid.pre_bid_offer),
      finalBid: parseFloat(bid.final_bid_offer),
      rank: rankLabel(mode, bid.bid_rank)
    }));

  useEffect(() => {
    const loadAuctions = async () => {
      if (!user?.id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const auctionReports = await ReportService.getAuctionReportsByUserId(user.id);
        
        const transformedAuctions: BaseAuction[] = auctionReports.map(report => ({
          id: report.id.toString(),
          auctionNo: `AUC-${report.id.toString().padStart(3, '0')}`,
          title: report.title,
          auctionDetails: report.auction_details || report.description || report.title,
          auctionDate: safeDateParse(report.auctionDate || report.auction_date),
          auctionStartTime: report.auctionStartTime || report.start_time || '10:00 AM',
          auctionEndTime: report.end_time || '11:00 AM', // ADD THIS
          duration: 60,
          currency: report.currency || 'INR',
          decrementalValue: report.decremental_value,
          auctionType: report.auction_type,
          auctionMode: report.auction_mode
        }));
        
        setAuctions(transformedAuctions);
        if (transformedAuctions.length > 0) {
          setSelectedAuction(transformedAuctions[0]);
          const firstReport = auctionReports[0];
          if (firstReport.bids && firstReport.bids.length > 0) {
            setParticipants(transformParticipantsData(firstReport.bids, firstReport.auction_mode));
          } else {
            setParticipants([]);
          }
        }
      } catch (err) {
        console.error('Error loading auction reports:', err);
        setError('Failed to load auction reports. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };
    loadAuctions();
  }, [user?.id]);

  useEffect(() => {
    const loadAuctionDetails = async () => {
      if (!selectedAuction || !user?.id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        try {
          const report = await ReportService.getAuctionReport(parseInt(selectedAuction.id));
          setSelectedAuction(transformAuctionData(report));
          if (report.bids && report.bids.length > 0) {
            setParticipants(transformParticipantsData(report.bids, report.auction_mode));
          } else {
            setParticipants([]);
          }
        } catch (reportErr) {
          console.warn('Individual auction report endpoint failed:', reportErr);
          setParticipants([]);
        }
      } catch (err) {
        console.error('Error loading auction details:', err);
        setError('Unable to load detailed auction information');
        setParticipants([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (selectedAuction) loadAuctionDetails();
  }, [selectedAuction?.id, user?.id]);

  const getEndTime = (auction: BaseAuction) => {
    try {
      // Convert 12-hour time to 24-hour for calculation
      const startTime24 = convert12to24(auction.auctionStartTime);
      
      // Parse auction date properly to avoid timezone issues
      let auctionDate: Date;
      if (auction.auctionDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const parts = auction.auctionDate.split('-');
        auctionDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      } else {
        auctionDate = new Date(auction.auctionDate);
      }
      
      // Extract time components
      const timeParts = startTime24.split(':');
      const hours = parseInt(timeParts[0]);
      const minutes = parseInt(timeParts[1]);
      
      // Create start time using the same date components to avoid timezone shifts
      const start = new Date(auctionDate.getFullYear(), auctionDate.getMonth(), auctionDate.getDate(), hours, minutes, 0);
      
      if (isNaN(start.getTime())) return new Date();
      return new Date(start.getTime() + (auction.duration || 60) * 60000);
    } catch {
      return new Date();
    }
  };

  // Updated to handle both 12-hour and 24-hour formats
  const formatTimeConsistent = (date: Date | string): string => {
    try {
      // If it's already a formatted 12-hour time string, return as is
      if (typeof date === 'string' && (date.includes('AM') || date.includes('PM'))) {
        return date;
      }
      
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return 'Invalid Time';

      // Format as 12-hour time
      let hours = dateObj.getHours();
      const minutes = dateObj.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      
      return `${hours}:${minutes} ${ampm}`;
    } catch {
      return 'Invalid Time';
    }
  };

  const formatDateConsistent = (date: Date | string): string => {
    try {
      let dateObj: Date;
      
      if (typeof date === 'string') {
        // Handle different date string formats
        if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // YYYY-MM-DD format - parse as local date to avoid timezone shifts
          const parts = date.split('-');
          dateObj = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        } else {
          dateObj = new Date(date);
        }
      } else {
        dateObj = date;
      }
      
      if (isNaN(dateObj.getTime())) return 'Invalid Date';

      // Use local date components to avoid timezone issues
      const day = dateObj.getDate().toString().padStart(2, '0');
      const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
      const year = dateObj.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return 'Invalid Date';
    }
  };

  const safeFormat = (date: Date | string, formatStr: string): string => {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return 'Invalid Date';
      return format(dateObj, formatStr);
    } catch {
      return 'Invalid Date';
    }
  };

  const handleDownloadPdf = async () => {
    if (!selectedAuction || isDownloading) return;
    setIsDownloading(true);
    setPdfError(null);
    try {
      await generateAuctionReport({ id: selectedAuction.id });
    } catch (err) {
      console.error('Failed to generate PDF report:', err);
      setPdfError('Could not create the PDF report. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const renderState = (subtitle: string, tone: 'neutral' | 'error' = 'neutral', retry = false) => (
    <div className="ap-reports-wrapper">
      <div className="ap-reports-state">
        <FileText className="ap-reports-state-icon" />
        <h1 className="ap-reports-state-title">Auction Reports</h1>
        <p className={`ap-reports-state-text ${tone === 'error' ? 'is-error' : ''}`}>
          {subtitle}
        </p>
        {retry && (
          <button
            className="ap-reports-btn"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );

  if (loading && !selectedAuction) return renderState('Loading auction reports...');
  if (!user) return renderState('Please log in to view your auction reports');
  if (error) return renderState(error, 'error', true);
  if (auctions.length === 0 && !loading) {
    return renderState('No auction reports found for your account');
  }

  // Flatten every participant's pre-bid / final bid into one list, then rank
  // lowest-first. A bid of 0 means "no bid placed", so it is left out rather
  // than being ranked as the lowest offer.
  // Forward auctions rank by the highest bid (H1, H2...), reverse by the lowest (L1, L2...).
  const isForward = isForwardMode(selectedAuction?.auctionMode);
  const topRank = rankLabel(selectedAuction?.auctionMode, 1);

  const rankedBids = participants
    .flatMap((participant) => {
      const bids: {
        id: string;
        companyName: string;
        bidAmount: number;
        bidType: string;
      }[] = [];
      if (participant.preBidOffer != null && participant.preBidOffer > 0) {
        bids.push({
          id: `${participant.id}-prebid`,
          companyName: participant.companyName,
          bidAmount: participant.preBidOffer,
          bidType: 'Pre-Bid',
        });
      }
      if (participant.finalBid != null && participant.finalBid > 0) {
        bids.push({
          id: `${participant.id}-finalbid`,
          companyName: participant.companyName,
          bidAmount: participant.finalBid,
          bidType: 'Final Bid',
        });
      }
      return bids;
    })
    // Best bid first: lowest for reverse auctions, highest for forward ones.
    .sort((a, b) => (isForward ? b.bidAmount - a.bidAmount : a.bidAmount - b.bidAmount))
    .map((bid, index) => ({ ...bid, rank: rankLabel(selectedAuction?.auctionMode, index + 1) }));

  const leadBid = rankedBids[0];
  const currency = selectedAuction?.currency || 'INR';

  return (
    <div className="ap-reports-wrapper">
      <div className="ap-reports-header">
        <div className="ap-reports-title-section">
          <h1 className="ap-reports-title">
            <FileText />
            Auction Reports
          </h1>
          <p className="ap-reports-subtitle">
            Detailed report for each of your auctions
          </p>
          {selectedAuction && (
            <div className="ap-reports-tags">
              <span className="ap-reports-tag ap-reports-tag-type">
                {auctionTypeLabel(selectedAuction.auctionType)}
              </span>
              <span className="ap-reports-tag ap-reports-tag-mode">
                {auctionModeLabel(selectedAuction.auctionMode)}
              </span>
            </div>
          )}
        </div>
        <div className="ap-reports-actions">
        <div className="ap-reports-picker">
          <label htmlFor="ap-reports-select" className="ap-reports-picker-label">
            Select auction
          </label>
          <select
            id="ap-reports-select"
            className="ap-reports-filter-select"
            value={selectedAuction?.id || ''}
            onChange={e => {
              const found = auctions.find(a => a.id === e.target.value);
              setSelectedAuction(found || null);
            }}
            disabled={loading}
          >
            {auctions.map(a => (
              <option key={a.id} value={a.id}>
                {a.auctionNo} - {a.title}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="ap-reports-download-btn"
          onClick={handleDownloadPdf}
          disabled={loading || isDownloading || !selectedAuction}
        >
          {isDownloading ? (
            <span className="ap-reports-spinner" />
          ) : (
            <Download />
          )}
          {isDownloading ? 'Generating...' : 'Download PDF'}
        </button>
        </div>
        {pdfError && (
          <p className="ap-reports-pdf-error" role="alert">
            {pdfError}
          </p>
        )}
      </div>

      {selectedAuction && (
        <>
          <section className="ap-reports-stats">
            <div className="ap-reports-stat ap-reports-stat-lead">
              <span className="ap-reports-stat-label">
                <Trophy /> {isForward ? 'Highest' : 'Lowest'} Bid ({topRank})
              </span>
              <span className="ap-reports-stat-value">
                {leadBid
                  ? `${currency} ${leadBid.bidAmount.toLocaleString()}`
                  : '—'}
              </span>
              <span className="ap-reports-stat-hint">
                {leadBid ? leadBid.companyName : 'No bids recorded'}
              </span>
            </div>
            <div className="ap-reports-stat">
              <span className="ap-reports-stat-label">
                <Gavel /> Total Bids
              </span>
              <span className="ap-reports-stat-value">{rankedBids.length}</span>
              <span className="ap-reports-stat-hint">Pre-bids and final bids</span>
            </div>
            <div className="ap-reports-stat">
              <span className="ap-reports-stat-label">
                <Users /> Participants
              </span>
              <span className="ap-reports-stat-value">{participants.length}</span>
              <span className="ap-reports-stat-hint">Companies invited</span>
            </div>
            <div className="ap-reports-stat">
              <span className="ap-reports-stat-label">
                <TrendingDown /> Decremental Value
              </span>
              <span className="ap-reports-stat-value">
                {selectedAuction.decrementalValue
                  ? `${currency} ${selectedAuction.decrementalValue}`
                  : 'N/A'}
              </span>
              <span className="ap-reports-stat-hint">Minimum step per bid</span>
            </div>
          </section>

          <div className="ap-reports-layout">
            <section className="ap-reports-card">
              <div className="ap-reports-card-header">
                <h2 className="ap-reports-card-title">
                  <Info /> Auction Details
                </h2>
              </div>
              <dl className="ap-reports-details">
                <div className="ap-reports-detail">
                  <dt>Auction No.</dt>
                  <dd>{selectedAuction.auctionNo}</dd>
                </div>
                <div className="ap-reports-detail">
                  <dt>Auction Type</dt>
                  <dd>{auctionTypeLabel(selectedAuction.auctionType)}</dd>
                </div>
                <div className="ap-reports-detail">
                  <dt>Format</dt>
                  <dd>{auctionModeLabel(selectedAuction.auctionMode)}</dd>
                </div>
                <div className="ap-reports-detail">
                  <dt>Date</dt>
                  <dd>{formatDateConsistent(selectedAuction.auctionDate)}</dd>
                </div>
                <div className="ap-reports-detail">
                  <dt>Start Time</dt>
                  <dd>{selectedAuction.auctionStartTime}</dd>
                </div>
                <div className="ap-reports-detail">
                  <dt>End Time</dt>
                  <dd>{selectedAuction.auctionEndTime}</dd>
                </div>
                <div className="ap-reports-detail">
                  <dt>Currency</dt>
                  <dd>{currency}</dd>
                </div>
                <div className="ap-reports-detail ap-reports-detail-block">
                  <dt>Description</dt>
                  <dd>{selectedAuction.auctionDetails || '—'}</dd>
                </div>
              </dl>
            </section>

            <section className="ap-reports-card">
              <div className="ap-reports-card-header">
                <h2 className="ap-reports-card-title">
                  <Trophy /> Bid Summary
                </h2>
                {rankedBids.length > 0 && (
                  <span className="ap-reports-count">
                    {rankedBids.length} {rankedBids.length === 1 ? 'bid' : 'bids'}
                  </span>
                )}
              </div>
              <div className="ap-reports-bid-table-wrapper">
                {loading ? (
                  <div className="ap-reports-empty">Loading bid details...</div>
                ) : rankedBids.length === 0 ? (
                  <div className="ap-reports-empty">
                    <p className="ap-reports-empty-title">
                      No bid data available for this auction
                    </p>
                    <p>
                      This may be because the auction hasn't started yet or
                      detailed bid information is not accessible.
                    </p>
                  </div>
                ) : (
                  <table className="ap-reports-bid-table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Participant Company</th>
                        <th>Bid Type</th>
                        <th className="ap-reports-num">Bid Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankedBids.map(bid => (
                        <tr key={bid.id} className={bid.rank === topRank ? 'ap-row-l1' : ''}>
                          <td data-label="Rank">
                            <span className={`rank-badge ${bid.rank === topRank ? 'rank-badge-l1' : ''}`}>
                              {bid.rank}
                            </span>
                          </td>
                          <td data-label="Company" className="ap-reports-company">
                            {bid.companyName}
                          </td>
                          <td data-label="Bid Type">
                            <span className={`ap-reports-type ${bid.bidType === 'Pre-Bid' ? 'is-pre' : 'is-final'}`}>
                              {bid.bidType}
                            </span>
                          </td>
                          <td data-label="Bid Amount" className="ap-reports-num">
                            {currency} {bid.bidAmount.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default AuctionReports;
